/**
 * Anonymous → authenticated pref migration handoff.
 *
 * Why a separate module: `claimAndSwitchToUser` (itinerary-scope.ts)
 * captures the anonymous session's pref choices BEFORE flipping the
 * persist scope. `UserPrefsSync.hydrate` (user-prefs-sync.ts) consumes
 * them AFTER the server has had its say (server-wins per field).
 * Putting the stash on `itinerary-scope.ts` would force user-prefs-sync
 * to import from there — creating a cycle (itinerary-scope already
 * imports UserPrefsSync).
 *
 * Semantics: any value left here is consumed by the next successful
 * post-sign-in hydrate. If hydrate is never called (network down at
 * sign-in time), the stash leaks until the next claim — acceptable;
 * the next set replaces the prior value.
 */
import type { LayerPrefRecord } from '@my-fullstack-app/itinerary-types';

/** Stashed snapshot of the anonymous scope's explicit toggles. Same
 *  shape as `UserPreferences.layerPrefs` on the wire so the migration
 *  arm in `UserPrefsSync.hydrate` can feed it directly into the
 *  per-explicit-id merge. See ADR-0082. */
export interface PendingPrefsMigration {
  layerPrefs?: LayerPrefRecord;
  reviewLayerPrefs?: LayerPrefRecord;
  hapticsEnabled?: boolean;
}

let _pending: PendingPrefsMigration | null = null;

/** Stash the anonymous-session pref values for the next post-sign-in
 *  hydrate to consume. Pass `null` (or an empty object) to clear. */
export function setPendingPrefsMigration(p: PendingPrefsMigration | null): void {
  _pending = p && Object.keys(p).length > 0 ? p : null;
}

/** Take and clear the stash. Returns whatever was set since the last
 *  consume, or `null` if nothing's pending. */
export function consumePendingPrefsMigration(): PendingPrefsMigration | null {
  const out = _pending;
  _pending = null;
  return out;
}
